import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { findParentNode } from '@tiptap/core';

import { Box, IconButton, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import TableChartIcon from '@mui/icons-material/TableChart';
import ImageIcon from '@mui/icons-material/Image';
import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import DeleteIcon from '@mui/icons-material/Delete';

import { mergeCells, splitCell, addRowAfter, addColumnAfter, deleteRow, deleteColumn } from 'prosemirror-tables';
import { useTheme } from "@mui/material/styles";

// Custom inline Image extension to improve HTML parsing/rendering with tables
const CustomImage = Image.extend({
  inline: true,
  group: 'inline',
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }: any) {
    return ['img', HTMLAttributes];
  },
});

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  textColor?: string;
}

const RichTextEditor: React.FC<Props> = ({ value, onChange, placeholder, textColor }) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Underline,
      Placeholder.configure({ placeholder: 'Description' }),
      BulletList,
      OrderedList,
      CustomImage,  // use custom inline image here
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    placeholder: placeholder,
    onUpdate: ({ editor }: any) => {
      onChange(editor.getHTML());
    },
    textColor: textColor
  });

  const [isInTable, setIsInTable] = useState(false);

  if (!editor) return null;

  const insertImageFromFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      if (!editor) return;
  
      // Ensure insertion point allows image insert, else set paragraph
      if (!editor.can().insertContent({ type: 'image', attrs: { src: base64 } })) {
        editor.chain().focus().setParagraph().run();
      }
  
      editor
        .chain()
        .focus()
        .setImage({ src: base64 as string })
        // Insert an empty paragraph node after image node
        .insertContent({
          type: 'paragraph',
          content: [],
        })
        // Move cursor inside the newly inserted paragraph node
        .command(({ tr, state, dispatch }: any) => {
          // Position cursor after image node, inside paragraph
          const newPos = tr.selection.to;
          dispatch?.(
            tr.setSelection(state.selection.constructor.near(tr.doc.resolve(newPos), 1))
          );
          return true;
        })
        .run();

        // Reset file input to allow same file selection again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
    };
    reader.readAsDataURL(file);
  };
  
  
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      insertImageFromFile(file);
    }
  };

  const handleAddRow = () => {
    editor.chain().focus().command(({ state, dispatch }: any) => addRowAfter(state, dispatch)).run();
  };
  const handleAddColumn = () => {
    editor.chain().focus().command(({ state, dispatch }: any) => addColumnAfter(state, dispatch)).run();
  };
  const handleDeleteRow = () => {
    editor.chain().focus().command(({ state, dispatch }: any) => deleteRow(state, dispatch)).run();
  };
  const handleDeleteColumn = () => {
    editor.chain().focus().command(({ state, dispatch }: any) => deleteColumn(state, dispatch)).run();
  };
  const handleMergeCells = () => {
    editor.chain().focus().command(({ state, dispatch }: any) => mergeCells(state, dispatch)).run();
  };
  const handleSplitCell = () => {
    editor.chain().focus().command(({ state, dispatch }: any) => splitCell(state, dispatch)).run();
  };

  const handleDeleteTable = () => {
    if (!editor) return;

    const tableNode = findParentNode(node => node.type.name === 'table')(editor.state.selection);

    if (tableNode) {
        editor
          .chain()
          .focus()
          .deleteRange({ from: tableNode.pos, to: tableNode.pos + tableNode.node.nodeSize })
          .insertContent('<p></p>') // Add paragraph so user can continue typing
          .setTextSelection(editor.state.doc.content.size) // Move cursor to end (inside that paragraph)
          .run();
      }
  };

  useEffect(() => {
    if (!editor) return;

    const checkTable = () => {
      const tableNode = findParentNode(node => node.type.name === 'table')(editor.state.selection);
      const inside = !!tableNode;
      setIsInTable(inside);
    };

    editor.on('selectionUpdate', checkTable);
    checkTable();

    return () => {
      editor.off('selectionUpdate', checkTable);
    };
  }, [editor]);

  return (
    <Box className="keep-border"
      sx={{
        border: `1px solid ${theme.palette.text.subtle}`,
        borderRadius: '4px',
        padding: 1,
        backgroundColor: 'transparent',
        color: `${theme.palette.textLight.main}`,
        minHeight: 180,
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* Toolbar buttons */}
        <Tooltip title="Bold">
          <IconButton onClick={() => editor.chain().focus().toggleBold().run()}>
            <FormatBoldIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important` }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic">
          <IconButton onClick={() => editor.chain().focus().toggleItalic().run()}>
            <FormatItalicIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important` }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline">
          <IconButton onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <FormatUnderlinedIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important` }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bullet List">
          <IconButton onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <FormatListBulletedIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important` }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered List">
          <IconButton onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <FormatListNumberedIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important` }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Image">
          <IconButton onClick={handleImageClick}>
            <ImageIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important` }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Insert Table">
          <IconButton
            onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()}
          >
            <TableChartIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important` }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add Row">
          <span>
            <IconButton onClick={handleAddRow} disabled={!isInTable}>
              <AddBoxIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important`, opacity: isInTable ? 1 : 0.5 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Add Column">
          <span>
            <IconButton onClick={handleAddColumn} disabled={!isInTable}>
              <AddBoxIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important`, transform: 'rotate(90deg)', opacity: isInTable ? 1 : 0.5 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete Row">
          <span>
            <IconButton onClick={handleDeleteRow} disabled={!isInTable}>
              <IndeterminateCheckBoxIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important`, opacity: isInTable ? 1 : 0.5 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete Column">
          <span>
            <IconButton onClick={handleDeleteColumn} disabled={!isInTable}>
              <IndeterminateCheckBoxIcon
                sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important`, transform: 'rotate(90deg)', opacity: isInTable ? 1 : 0.5 }}
              />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Merge Cells">
          <span>
            <IconButton onClick={handleMergeCells} disabled={!isInTable}>
              <MergeTypeIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important`, opacity: isInTable ? 1 : 0.5 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Split Cell">
          <span>
            <IconButton onClick={handleSplitCell} disabled={!isInTable}>
              <MergeTypeIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important`, transform: 'rotate(180deg)', opacity: isInTable ? 1 : 0.5 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete Table">
          <span>
            <IconButton onClick={handleDeleteTable} disabled={!isInTable}>
              <DeleteIcon sx={{ color: `${theme.palette.richTextEditor.icons.primary} !important`, opacity: isInTable ? 1 : 0.5 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        className="tiptap-editor"
        sx={{
          position: 'relative',
          backgroundColor: 'transparent',
          minHeight: 180,
          color: textColor != '' ? textColor : `${theme.palette.textLight.main}`,
          borderRadius: 4,
          padding: '0 8px',
          border: 'none',
          outline: 'none',
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <style>{`
        .tiptap-editor table {
          width: 100%;
          border-collapse: collapse;
          border: 2px solid ${theme.palette.textLight.secondary};
          background-color: transparent;
        }

        .tiptap-editor th, .tiptap-editor td {
          border: 1px solid ${theme.palette.textLight.secondary};
          px: 8px;
          text-align: left;
        }

        .tiptap-editor th{
          font-weight: bold;
        }

        .tiptap-editor img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 8px 0;
          object-fit: contain;
        }

        .ProseMirror {
            min-height: 10em; /* roughly 4 lines assuming 1.5em line-height */
            line-height: 1.5em;
            padding: 8px;
            margin: 0;
        }
        
        /* Remove margin from paragraphs inside editor */
        .ProseMirror p {
            margin: 0;
        }

        /* Placeholder styling */
        p.is-empty.is-editor-empty::before {
            content: attr(data-placeholder);
            color: ${theme.palette.textLight.main};
            position: absolute;
            pointer-events: none;
            user-select: none;
            top: 8px;
            z-index: 1;
        }
      `}</style>
    </Box>
  );
};

export default RichTextEditor;
